import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatDateOnly } from "@/lib/date-only";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from "@/lib/api";
import { deleteWithUndo } from "@/lib/delete-with-undo";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { UserRecords } from "@/components/admin/user-records";
import { Badge } from "@/components/ui/badge";
import { SubWorkersList, type SubPerson } from "@/components/subs/sub-workers-list";
import { SubWorkerProfile } from "@/components/subs/sub-worker-profile";
import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { divisionBadgeClass } from "@/lib/division-color";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  UserPlus,
  Loader2,
  Download,
  Upload,
  CheckCircle2,
  AlertCircle,
  Pencil,
  KeyRound,
  Ban,
  Trash2,
  UserCheck,
  Eye,
  Cake,
  Briefcase,
  Camera,
  MessageSquarePlus,
  StickyNote,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ShieldCheck,
} from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/admin/users")({
  // ?open=<userId> opens that person's profile sheet (used by links from other admin pages)
  validateSearch: (s: Record<string, unknown>): { open?: string } =>
    typeof s.open === "string" ? { open: s.open } : {},
  component: UsersPage,
});

type Division = {
  id: string;
  name: string;
  parentId: string | null;
  allowDivAdmin?: boolean;
  color?: string | null;
};

type User = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  divisionId: string | null;
  divisionName: string | null;
  divisionIds?: string[];
  isSupervisor?: boolean;
  banned?: boolean;
  banReason?: string | null;
  birthDate?: string | null;
  jobTitleId?: string | null;
  jobTitleName?: string | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  emergencyContactRelationship?: string | null;
  profilePhotoFileId?: string | null;
};

type Role = "field_personnel" | "office_personnel" | "div_admin" | "admin" | "subcontractor" | "contractor";

const ROLE_LABELS: Record<Role, string> = {
  field_personnel: "Field Personnel",
  office_personnel: "Office Personnel",
  div_admin: "Division Admin",
  admin: "Super Admin",
  subcontractor: "Subcontractor",
  contractor: "Contractor",
};

// Roles shown in the Add User form (simple role picker, no division encoding)
const FORM_ROLES: Role[] = ["field_personnel", "office_personnel", "div_admin", "admin", "subcontractor", "contractor"];

const roleBadgeClass: Record<Role, string> = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400 border-transparent",
  div_admin: "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border-transparent",
  office_personnel: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-transparent",
  field_personnel: "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-transparent",
  subcontractor: "bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 border-transparent",
  contractor: "bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border-transparent",
};

/** Badge label for the current account type */
function accountTypeBadgeLabel(user: User): string {
  return ROLE_LABELS[user.role as Role] ?? user.role;
}

/** CSS classes for the role badge */
function accountTypeBadgeClass(role: string): string {
  return roleBadgeClass[role as Role] ?? "bg-gray-100 text-gray-600 border-transparent";
}

type BulkResult = {
  created: number;
  updated: number;
  errors: string[];
};

const BASE_URL = import.meta.env.VITE_API_URL || "";

/**
 * The Users page: the Paris crew (the default), the subcontractors, or everybody together. One counter at the top
 * says how many people there are in all and how many of each kind.
 */
function UsersPage() {
  const [view, setView] = useState<"crew" | "subs" | "all">("crew");
  const navigate = useNavigate();
  const tabBtn = (active: boolean) =>
    cn(
      "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
      active ? "bg-surface-raised text-text-primary shadow-sm" : "text-muted-foreground hover:text-text-primary"
    );

  const { data: crewData } = useQuery({ queryKey: ["admin", "users-all-crew"], queryFn: () => apiGet<{ data: User[] }>("/api/users"), staleTime: 30_000 });
  const { data: subData } = useQuery({ queryKey: ["sub-workers"], queryFn: () => apiGet<{ data: SubPerson[] }>("/api/subs/workers"), staleTime: 15_000 });
  const crew = (crewData?.data ?? []).filter((u) => u.banReason !== "pending_approval");
  const subs = subData?.data ?? [];
  const ready = !!crewData && !!subData;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex w-fit gap-1 rounded-lg border border-border bg-muted/30 p-1">
          <button type="button" className={tabBtn(view === "crew")} onClick={() => setView("crew")}>Paris Crew</button>
          <button type="button" className={tabBtn(view === "subs")} onClick={() => setView("subs")}>Subcontractors</button>
          <button type="button" className={tabBtn(view === "all")} onClick={() => setView("all")}>All users</button>
        </div>
        {ready && (
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-text-primary">{crew.length + subs.length} total users</span> · {crew.length} Paris crew · {subs.length} subcontractor{subs.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>
      {view === "crew" && <CrewUsers />}
      {view === "subs" && <SubWorkersList />}
      {view === "all" && (
        <AllUsers
          crew={crew}
          subs={subs}
          onOpenCrew={(id) => {
            setView("crew");
            navigate({ to: "/admin/users", search: { open: id } });
          }}
        />
      )}
    </div>
  );
}

/** Everybody in one list: Paris crew and subcontractors, each with a badge for the kind of user. */
function AllUsers({ crew, subs, onOpenCrew }: { crew: User[]; subs: SubPerson[]; onOpenCrew: (id: string) => void }) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<"all" | "crew" | "subs">("all");
  const [openSub, setOpenSub] = useState<string | null>(null);

  type Row = { key: string; id: string; kind: "crew" | "subs"; name: string; sub: string; badge: string; badgeClass: string; company: string };
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [
      ...crew.map((u): Row => ({
        key: `c-${u.id}`,
        id: u.id,
        kind: "crew",
        name: u.name,
        sub: [u.jobTitleName, u.divisionName, u.phone, u.email].filter(Boolean).join(" · "),
        badge: ROLE_LABELS[u.role as Role] ?? "Paris crew",
        badgeClass: roleBadgeClass[u.role as Role] ?? "bg-gray-100 text-gray-600 border-transparent",
        company: "Paris",
      })),
      ...subs.map((p): Row => ({
        key: `s-${p.userId}`,
        id: p.userId,
        kind: "subs",
        name: p.name,
        sub: [p.trade, p.phone, p.email].filter(Boolean).join(" · "),
        badge: "Subcontractor",
        badgeClass: roleBadgeClass.subcontractor,
        company: p.companies.map((c) => c.companyName).join(", "),
      })),
    ];
    return out.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [crew, subs]);
  const shown = rows.filter((r) => (kind === "all" || r.kind === kind) && matchesQuery(query, r.name, r.sub, r.company, r.badge));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[12rem] flex-1"><PickerSearch value={query} onChange={setQuery} placeholder="Search name, company, trade, role..." /></div>
        <div className="flex gap-1">
          {([["all", "Everyone"], ["crew", "Paris crew"], ["subs", "Subcontractors"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setKind(k)} className={cn("rounded-full px-3 py-1 text-xs font-medium", kind === k ? "bg-paris-red text-white" : "border border-border bg-card text-text-secondary hover:bg-muted/50")}>{label}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{shown.length} shown</p>
      <div className="divide-y rounded-lg border bg-card">
        {shown.map((r) => (
          <button key={r.key} type="button" onClick={() => (r.kind === "crew" ? onOpenCrew(r.id) : setOpenSub(r.id))} className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-muted/30">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {r.name}
                <span className={cn("ml-2 rounded-full border px-2 py-0.5 text-[11px] font-medium", r.badgeClass)}>{r.badge}</span>
              </p>
              <p className="truncate text-xs text-muted-foreground">{r.sub}</p>
            </div>
            {r.kind === "subs" && r.company && <p className="min-w-0 truncate text-xs text-muted-foreground sm:w-48">{r.company}</p>}
          </button>
        ))}
        {shown.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">Nobody matches.</p>}
      </div>
      <SubWorkerProfile userId={openSub} onClose={() => setOpenSub(null)} />
    </div>
  );
}

function CrewUsers() {
  const { data: session } = authClient.useSession();
  const isSuperAdmin = session?.user?.role === "admin";
  const queryClient = useQueryClient();
  const { open: openUserId } = Route.useSearch();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "field_personnel" as Role,
    divisionIds: [] as string[],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit sheet state
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", divisionIds: [] as string[], jobTitleId: "" });
  const [saving, setSaving] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteUserConfirm, setDeleteUserConfirm] = useState<{ id: string; name: string; email: string } | null>(null);

  // Pending approval detail sheet
  const [pendingDetailUser, setPendingDetailUser] = useState<User | null>(null);
  const [pendingRole, setPendingRole] = useState("field_personnel");
  const [pendingDivisionIds, setPendingDivisionIds] = useState<string[]>([]);
  const [pendingJobTitleId, setPendingJobTitleId] = useState("");
  const [pendingNote, setPendingNote] = useState("");
  const [approvingDetail, setApprovingDetail] = useState(false);
  const [rejectingDetail, setRejectingDetail] = useState(false);
  const [pendingPhotoOpen, setPendingPhotoOpen] = useState(false);
  const [editPhotoOpen, setEditPhotoOpen] = useState(false);

  // Search / filter / sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDivId, setFilterDivId] = useState("");
  const [filterJobTitleId, setFilterJobTitleId] = useState("");
  type SortField = "name" | "jobTitle" | "division" | "role";
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ChevronsUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc"
      ? <ChevronUp className="h-3 w-3 ml-1" />
      : <ChevronDown className="h-3 w-3 ml-1" />;
  }

  const activeUsers = useMemo(
    () => users.filter((u) => u.banReason !== "pending_approval"),
    [users]
  );

  const displayUsers = useMemo(() => {
    let list = activeUsers;
    const q = searchQuery.trim().toLowerCase();
    if (q) list = list.filter((u) =>
      u.name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      (u.jobTitleName?.toLowerCase().includes(q) ?? false) ||
      (u.divisionName?.toLowerCase().includes(q) ?? false)
    );
    if (filterDivId) list = list.filter((u) =>
      u.divisionId === filterDivId || u.divisionIds?.includes(filterDivId)
    );
    if (filterJobTitleId) list = list.filter((u) => u.jobTitleId === filterJobTitleId);
    if (sortField) {
      list = [...list].sort((a, b) => {
        const av =
          sortField === "name" ? a.name :
          sortField === "jobTitle" ? (a.jobTitleName ?? "") :
          sortField === "division" ? (a.divisionName ?? "") :
          a.role;
        const bv =
          sortField === "name" ? b.name :
          sortField === "jobTitle" ? (b.jobTitleName ?? "") :
          sortField === "division" ? (b.divisionName ?? "") :
          b.role;
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return list;
  }, [activeUsers, searchQuery, filterDivId, filterJobTitleId, sortField, sortDir]);

  const hasFilters = searchQuery || filterDivId || filterJobTitleId;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiGet<{ data: User[] }>("/api/users");
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDivisions = useCallback(async () => {
    try {
      const res = await apiGet<{ data: Division[] }>("/api/admin/divisions");
      setDivisions(res.data);
    } catch {
      // Divisions may not exist yet — non-blocking
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchDivisions();
  }, [fetchUsers, fetchDivisions]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error("All fields are required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setCreating(true);
    try {
      await apiPost("/api/admin/users", {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        role: form.role,
        divisionIds: form.divisionIds,
      });
      toast.success(`${form.name} added successfully`);
      setForm({ name: "", email: "", password: "", phone: "", role: "field_personnel", divisionIds: [] });
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId: string, value: string) => {
    const prevUsers = [...users];
    try {
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: value } : u))
      );
      await apiPatch(`/api/admin/users/${userId}`, { role: value });
      toast.success("Account type updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "divisions"] });
    } catch (err) {
      setUsers(prevUsers);
      toast.error(err instanceof Error ? err.message : "Failed to update account type");
    }
  };

  const handleDivisionChange = async (userId: string, divisionId: string) => {
    const newDivisionId = divisionId === "none" ? null : divisionId;
    try {
      await apiPatch(`/api/admin/users/${userId}`, { divisionId: newDivisionId });
      const divName = divisions.find((d) => d.id === newDivisionId)?.name ?? null;
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, divisionId: newDivisionId, divisionName: divName }
            : u
        )
      );
      toast.success("Division updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update division");
    }
  };

  const openEditSheet = (user: User) => {
    setEditUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      divisionIds: user.divisionIds ?? (user.divisionId ? [user.divisionId] : []),
      jobTitleId: user.jobTitleId ?? "",
    });
    setResetPw("");
  };

  // Arriving from a link (?open=<userId>): open that person's profile once the list is in.
  useEffect(() => {
    if (!openUserId || loading) return;
    const target = users.find((u) => u.id === openUserId);
    if (target) openEditSheet(target);
    navigate({ to: "/admin/users", search: {}, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUserId, loading, users]);

  const handleSaveEdit = async () => {
    if (!editUser) return;
    if (!editForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editForm.email.trim()) {
      toast.error("Email is required");
      return;
    }

    setSaving(true);
    try {
      // PUT updates name/email/phone (general fields)
      await apiPut(`/api/admin/users/${editUser.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || null,
      });
      // PATCH updates divisions + job title (separate endpoint that handles join table)
      await apiPatch(`/api/admin/users/${editUser.id}`, {
        divisionIds: editForm.divisionIds,
        jobTitleId: editForm.jobTitleId || null,
      });

      const primaryDivisionId = editForm.divisionIds[0] || null;
      const primaryDivisionName = primaryDivisionId
        ? divisions.find((d) => d.id === primaryDivisionId)?.name ?? null
        : null;
      const newJobTitleName = editForm.jobTitleId
        ? jobTitles.find((jt) => jt.id === editForm.jobTitleId)?.name ?? null
        : null;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editUser.id
            ? {
                ...u,
                name: editForm.name.trim(),
                email: editForm.email.trim(),
                phone: editForm.phone.trim() || null,
                divisionId: primaryDivisionId,
                divisionName: primaryDivisionName,
                divisionIds: [...editForm.divisionIds],
                jobTitleId: editForm.jobTitleId || null,
                jobTitleName: newJobTitleName,
              }
            : u
        )
      );
      toast.success("User updated");
      setEditUser(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editUser) return;
    if (resetPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setResetting(true);
    try {
      await apiPost(`/api/admin/users/${editUser.id}/reset-password`, {
        password: resetPw,
      });
      toast.success(`Password reset for ${editUser.name}. They will need to log in again.`);
      setResetPw("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const handleToggleDeactivate = async () => {
    if (!editUser) return;
    setToggling(true);
    try {
      const res = await apiPost<{ banned: boolean }>(
        `/api/admin/users/${editUser.id}/deactivate`,
        {}
      );
      const newBanned = res.banned;
      setUsers((prev) =>
        prev.map((u) => (u.id === editUser.id ? { ...u, banned: newBanned } : u))
      );
      setEditUser((prev) => prev ? { ...prev, banned: newBanned } : null);
      toast.success(newBanned ? `${editUser.name} deactivated` : `${editUser.name} reactivated`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setToggling(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      await apiPost(`/api/admin/users/${userId}/approve`, { role: "field_personnel" });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, banned: false, banReason: null } : u
        )
      );
      queryClient.invalidateQueries({ queryKey: ["approvals", "pending"] });
      toast.success("User approved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId: string) => {
    if (!window.confirm("Reject this signup? The account will be marked as rejected. Super admins can permanently delete it later.")) return;
    try {
      await apiPost(`/api/admin/users/${userId}/reject`, {});
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, banned: true, banReason: "rejected" } : u));
      queryClient.invalidateQueries({ queryKey: ["approvals", "pending"] });
      toast.success("Signup rejected");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reject signup");
    }
  };

  const handleDeleteUser = async () => {
    if (!editUser) return;
    setDeleting(true);
    try {
      await apiDelete(`/api/admin/users/${editUser.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== editUser.id));
      toast.success(`${editUser.name} deleted permanently`);
      setEditUser(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    const res = await fetch(`${BASE_URL}/api/admin/users/template`, { credentials: "include" });
    if (!res.ok) { toast.error("Failed to download template"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "user-upload-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setBulkResult(null);

    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);

      if (lines.length < 2) {
        toast.error("CSV must have a header row and at least one data row");
        return;
      }

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const nameIdx = headers.indexOf("name");
      const emailIdx = headers.indexOf("email");
      const passwordIdx = headers.indexOf("password");
      const roleIdx = headers.indexOf("role");
      const divisionIdx = headers.indexOf("division");

      if (nameIdx === -1 || emailIdx === -1) {
        toast.error("CSV must have 'name' and 'email' columns");
        return;
      }

      const rows = lines.slice(1).map((line) => {
        const cols = parseCSVLine(line);
        return {
          name: cols[nameIdx]?.trim() || "",
          email: cols[emailIdx]?.trim() || "",
          password: passwordIdx >= 0 ? (cols[passwordIdx]?.trim() || "") : "",
          role: roleIdx >= 0 ? (cols[roleIdx]?.trim() || "field_personnel") : "field_personnel",
          division: divisionIdx >= 0 ? (cols[divisionIdx]?.trim() || "") : "",
        };
      });

      const res = await apiPost<{ data: BulkResult }>("/api/admin/users/bulk", { rows });
      setBulkResult(res.data);

      if (res.data.created > 0 || res.data.updated > 0) {
        fetchUsers();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload CSV");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const { data: jobTitlesData } = useQuery({
    queryKey: ["job-titles-list"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/admin/job-titles"),
  });
  const jobTitles = jobTitlesData?.data ?? [];

  const { data: pendingPhotoUrlData } = useQuery({
    queryKey: ["file-url", pendingDetailUser?.profilePhotoFileId],
    queryFn: () => apiGet<{ url: string }>(`/api/files/${pendingDetailUser!.profilePhotoFileId}/url`),
    enabled: !!pendingDetailUser?.profilePhotoFileId,
    staleTime: 10 * 60_000,
  });

  const { data: editPhotoUrlData } = useQuery({
    queryKey: ["file-url", editUser?.profilePhotoFileId],
    queryFn: () => apiGet<{ url: string }>(`/api/files/${editUser!.profilePhotoFileId}/url`),
    enabled: !!editUser?.profilePhotoFileId,
    staleTime: 10 * 60_000,
  });

  type NoteResponse = "acknowledged" | "completed" | "need_clarification";
  type WorkerNote = { id: string; content: string; isReadByWorker: boolean; response: NoteResponse | null; respondedAt: string | null; createdAt: string; authorName: string };
  const { data: notesData, refetch: refetchNotes } = useQuery({
    queryKey: ["worker-notes", editUser?.id],
    queryFn: () => apiGet<{ data: WorkerNote[] }>(`/api/admin/users/${editUser!.id}/notes`),
    enabled: !!editUser,
  });
  const workerNotes = notesData?.data ?? [];
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const handleSaveNote = async () => {
    if (!editUser || !newNote.trim()) return;
    setSavingNote(true);
    try {
      await apiPost(`/api/admin/users/${editUser.id}/notes`, { content: newNote.trim() });
      setNewNote("");
      refetchNotes();
      toast.success("Note saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  function openPendingDetail(u: User) {
    setPendingDetailUser(u);
    setPendingRole("field_personnel");
    setPendingDivisionIds(u.divisionIds ?? (u.divisionId ? [u.divisionId] : []));
    setPendingJobTitleId(u.jobTitleId ?? "");
    setPendingNote("");
  }

  const handleApproveFromDetail = async () => {
    if (!pendingDetailUser) return;
    setApprovingDetail(true);
    try {
      await apiPost(`/api/admin/users/${pendingDetailUser.id}/approve`, {
        role: pendingRole,
        divisionIds: pendingDivisionIds.length > 0 ? pendingDivisionIds : undefined,
        jobTitleId: pendingJobTitleId || pendingDetailUser.jobTitleId || null,
        note: pendingNote.trim() || undefined,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === pendingDetailUser.id ? { ...u, banned: false, banReason: null } : u
        )
      );
      queryClient.invalidateQueries({ queryKey: ["approvals", "pending"] });
      toast.success("Worker approved");
      setPendingDetailUser(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to approve user");
    } finally {
      setApprovingDetail(false);
    }
  };

  const handleRejectFromDetail = async () => {
    if (!pendingDetailUser) return;
    setRejectingDetail(true);
    try {
      await apiPost(`/api/admin/users/${pendingDetailUser.id}/reject`, {
        note: pendingNote.trim() || undefined,
      });
      setUsers((prev) => prev.map((u) => u.id === pendingDetailUser.id ? { ...u, banned: true, banReason: "rejected" } : u));
      queryClient.invalidateQueries({ queryKey: ["approvals", "pending"] });
      toast.success("Signup rejected");
      setPendingDetailUser(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reject signup");
    } finally {
      setRejectingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingUsers = users.filter(
    (u) => u.banned && u.banReason === "pending_approval"
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Users</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage worker accounts and permissions.</p>
        </div>
      </div>

      {/* Pending approvals banner */}
      {pendingUsers.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-amber-700 dark:text-amber-400" />
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
              {pendingUsers.length} pending signup{pendingUsers.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="space-y-2">
            {pendingUsers.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-2 bg-card border border-border rounded-md px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {u.email}
                    {u.phone && <span className="ml-2">{u.phone}</span>}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPendingDetail(u)}
                    className="h-8"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleApproveUser(u.id)}
                    className="h-8"
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRejectUser(u.id)}
                    className="h-8 text-destructive hover:text-destructive"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          {users.length} Paris crew user{users.length !== 1 ? "s" : ""}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-1" />
            CSV Template
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-1" />
            )}
            Upload CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button
            size="sm"
            onClick={() => setShowForm(!showForm)}
            variant={showForm ? "outline" : "default"}
          >
            {showForm ? (
              "Cancel"
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" />
                Add User
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bulk upload results */}
      {bulkResult && (
        <div className="rounded-lg border bg-card p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Upload Complete
          </div>
          <div className="flex gap-4 text-sm">
            <span className="text-green-700">{bulkResult.created} created</span>
            <span className="text-blue-700">{bulkResult.updated} updated</span>
            {bulkResult.errors.length > 0 && (
              <span className="text-red-600">{bulkResult.errors.length} errors</span>
            )}
          </div>
          {bulkResult.errors.length > 0 && (
            <div className="mt-2 space-y-1">
              {bulkResult.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  {err}
                </div>
              ))}
            </div>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => setBulkResult(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Add user form */}
      {showForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-lg border bg-card p-4 space-y-3"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
            <UserPlus className="h-4 w-4" />
            New User
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <Input
              type="password"
              placeholder="Password (min 8 chars)"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
            <Input
              type="tel"
              placeholder="Phone (for password reset)"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <Select
              value={form.role}
              onValueChange={(v) => setForm((f) => ({ ...f, role: v as Role }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORM_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {divisions.length > 0 && (
            <DivisionMultiPicker
              divisions={divisions}
              selected={form.divisionIds}
              onChange={(ids) => setForm((f) => ({ ...f, divisionIds: ids }))}
            />
          )}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Create User
            </Button>
          </div>
        </form>
      )}

      {/* Search / filter bar */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search name, email, job title, division…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        {divisions.length > 0 && (
          <div className="w-44">
            <SearchableSelect
              size="sm"
              value={filterDivId}
              onChange={setFilterDivId}
              allLabel="All divisions"
              allValue=""
              options={divisions.map((d) => ({ value: d.id, label: d.name }))}
            />
          </div>
        )}
        {jobTitles.length > 0 && (
          <div className="w-48">
            <SearchableSelect
              size="sm"
              value={filterJobTitleId}
              onChange={setFilterJobTitleId}
              allLabel="All job titles"
              allValue=""
              options={jobTitles.map((jt) => ({ value: jt.id, label: jt.name }))}
            />
          </div>
        )}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-muted-foreground"
            onClick={() => { setSearchQuery(""); setFilterDivId(""); setFilterJobTitleId(""); }}
          >
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* User list */}
      <div className="rounded-lg border bg-card divide-y">
        {/* Column header — sortable */}
        <div className="hidden sm:flex items-center px-4 py-2 gap-3 bg-muted/40 text-[11px] font-medium text-muted-foreground uppercase tracking-wide rounded-t-lg">
          <button onClick={() => toggleSort("name")} className="flex-1 flex items-center hover:text-foreground transition-colors text-left">
            Name / Email <SortIcon field="name" />
          </button>
          <button onClick={() => toggleSort("jobTitle")} className="w-[150px] flex items-center justify-end hover:text-foreground transition-colors">
            Job Title <SortIcon field="jobTitle" />
          </button>
          {divisions.length > 0 && (
            <button onClick={() => toggleSort("division")} className="w-[200px] flex items-center justify-end hover:text-foreground transition-colors">
              Division <SortIcon field="division" />
            </button>
          )}
          <button onClick={() => toggleSort("role")} className="w-[210px] flex items-center justify-end hover:text-foreground transition-colors">
            Account Type <SortIcon field="role" />
          </button>
        </div>
        {activeUsers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No users yet. Add the first user above.
          </div>
        ) : displayUsers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No users match your filters.
          </div>
        ) : (
          displayUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between px-4 py-3 gap-3"
            >
              <button
                type="button"
                className="min-w-0 flex-1 text-left group"
                onClick={() => openEditSheet(user)}
              >
                <p className={cn(
                  "font-medium text-[13px] leading-tight break-words sm:text-sm sm:truncate group-hover:text-paris-red-text transition-colors",
                  user.banned && "line-through opacity-60"
                )}>
                  {user.name}
                  {user.banReason === "rejected" && (
                    <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 border-orange-400 text-orange-600">
                      Rejected
                    </Badge>
                  )}
                  {user.banned && user.banReason !== "rejected" && (
                    <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 border-red-300 text-red-600">
                      Deactivated
                    </Badge>
                  )}
                  <Pencil className="h-3 w-3 inline ml-1.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                </p>
                <p className="text-[11px] text-muted-foreground truncate sm:text-xs">
                  {user.email}
                </p>
              </button>
              {/* Job Title */}
              <div className="hidden sm:block w-[150px] shrink-0 text-right">
                <span className="text-xs text-muted-foreground truncate">
                  {user.jobTitleName ?? "—"}
                </span>
              </div>
              {/* Division chips (read-only display; edit via sheet) */}
              {divisions.length > 0 && (
                <div className="hidden sm:flex flex-wrap gap-1 w-[200px] justify-end shrink-0">
                  {(user.divisionIds && user.divisionIds.length > 0
                    ? user.divisionIds
                    : user.divisionId
                      ? [user.divisionId]
                      : []
                  ).map((divId, idx) => {
                    const div = divisions.find((d) => d.id === divId);
                    if (!div) return null;
                    return (
                      <span
                        key={divId}
                        className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full border",
                          divisionBadgeClass(divId, divisions),
                          idx === 0 && "font-semibold"
                        )}
                      >
                        {div.name}
                      </span>
                    );
                  })}
                  {(!user.divisionIds || user.divisionIds.length === 0) && !user.divisionId && (
                    <span className="text-[10px] text-muted-foreground italic">
                      No division
                    </span>
                  )}
                </div>
              )}
              {/* Supervisor indicator + Account Type dropdown */}
              <div className="flex items-center gap-1.5 shrink-0">
                {user.isSupervisor && (
                  <span title="Supervisor" className="flex items-center justify-center h-6 w-6 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 shrink-0">
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </span>
                )}
              <Select
                value={user.role}
                onValueChange={(v) => handleRoleChange(user.id, v)}
                disabled={
                  isSuperAdmin
                    ? false
                    : user.role !== "field_personnel" && user.role !== "office_personnel"
                }
              >
                <SelectTrigger className="h-7 w-[92px] shrink-0 gap-0.5 px-1.5 sm:h-8 sm:w-[180px] sm:gap-2 sm:px-3">
                  <Badge className={cn("min-w-0 truncate px-1.5 text-[10px] whitespace-nowrap sm:px-2.5 sm:text-xs", accountTypeBadgeClass(user.role))}>
                    {accountTypeBadgeLabel(user)}
                  </Badge>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="field_personnel">Field Personnel</SelectItem>
                  <SelectItem value="office_personnel">Office Personnel</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  {isSuperAdmin && <SelectItem value="div_admin">Division Admin</SelectItem>}
                  {isSuperAdmin && <SelectItem value="admin">Super Admin</SelectItem>}
                </SelectContent>
              </Select>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pending worker detail sheet */}
      <Sheet open={!!pendingDetailUser} onOpenChange={(open) => { if (!open) { setPendingDetailUser(null); setPendingPhotoOpen(false); } }}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Pending Worker</SheetTitle>
          </SheetHeader>
          {pendingDetailUser && (
            <div className="space-y-5 mt-4 pb-6">
              {/* Photo + name */}
              <div className="flex items-center gap-4">
                {pendingPhotoUrlData?.url ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setPendingPhotoOpen(true)}
                      className="cursor-zoom-in rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <img
                        src={pendingPhotoUrlData.url}
                        alt={pendingDetailUser.name}
                        className="h-16 w-16 rounded-full object-cover border border-border"
                      />
                    </button>
                    {pendingPhotoOpen && createPortal(
                      <div
                        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
                        onClick={() => setPendingPhotoOpen(false)}
                      >
                        <img
                          src={pendingPhotoUrlData.url}
                          alt={pendingDetailUser.name}
                          className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                        />
                      </div>,
                      document.body
                    )}
                  </>
                ) : (
                  <div className="h-16 w-16 rounded-full bg-surface-hover flex items-center justify-center border border-border">
                    <Camera className="h-6 w-6 text-muted-foreground/40" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-lg">{pendingDetailUser.name}</p>
                  <p className="text-sm text-muted-foreground">{pendingDetailUser.email}</p>
                  {pendingDetailUser.phone && (
                    <p className="text-sm text-muted-foreground">{pendingDetailUser.phone}</p>
                  )}
                </div>
              </div>

              {/* Profile details */}
              <div className="space-y-1.5 text-sm">
                {pendingDetailUser.jobTitleName && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-3.5 w-3.5 shrink-0" />
                    <span>{pendingDetailUser.jobTitleName}</span>
                  </div>
                )}
                {pendingDetailUser.birthDate && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Cake className="h-3.5 w-3.5 shrink-0" />
                    <span>DOB: {formatDateOnly(pendingDetailUser.birthDate, undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                  </div>
                )}
              </div>

              {pendingDetailUser.emergencyContactName && (
                <div className="rounded-md border border-border bg-surface-base/50 px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Emergency Contact</p>
                  <p className="text-sm font-medium">
                    {pendingDetailUser.emergencyContactName}
                    {pendingDetailUser.emergencyContactRelationship && (
                      <span className="text-muted-foreground font-normal ml-1">
                        ({pendingDetailUser.emergencyContactRelationship})
                      </span>
                    )}
                  </p>
                  {pendingDetailUser.emergencyContactPhone && (
                    <p className="text-sm text-muted-foreground">{pendingDetailUser.emergencyContactPhone}</p>
                  )}
                </div>
              )}

              {/* Assign section */}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Assign before approving
                </p>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Role</label>
                  <select
                    value={pendingRole}
                    onChange={(e) => setPendingRole(e.target.value)}
                    disabled={approvingDetail || rejectingDetail}
                    className="w-full h-9 rounded-md border border-border bg-surface-base px-2 text-sm"
                  >
                    {(isSuperAdmin ? FORM_ROLES : (["field_personnel", "office_personnel"] as Role[])).map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </div>

                {jobTitles.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Job Title</label>
                    <SearchableSelect
                      value={pendingJobTitleId}
                      onChange={setPendingJobTitleId}
                      disabled={approvingDetail || rejectingDetail}
                      allLabel={`— Keep as registered (${pendingDetailUser.jobTitleName ?? "none"}) —`}
                      allValue=""
                      options={jobTitles.map((jt) => ({ value: jt.id, label: jt.name }))}
                    />
                  </div>
                )}

                {divisions.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Divisions</label>
                    <DivisionMultiPicker
                      divisions={divisions}
                      selected={pendingDivisionIds}
                      onChange={setPendingDivisionIds}
                    />
                  </div>
                )}
              </div>

              {/* Optional note to the worker */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Note to worker <span className="normal-case font-normal">(optional)</span>
                </label>
                <textarea
                  value={pendingNote}
                  onChange={(e) => setPendingNote(e.target.value)}
                  disabled={approvingDetail || rejectingDetail}
                  placeholder="Add a message that will be visible to the worker on their dashboard…"
                  rows={3}
                  className="w-full rounded-md border border-border bg-surface-base px-3 py-2 text-sm placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleRejectFromDetail}
                  disabled={approvingDetail || rejectingDetail}
                >
                  {rejectingDetail && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Reject
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleApproveFromDetail}
                  disabled={approvingDetail || rejectingDetail}
                >
                  {approvingDetail && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Approve
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit User Sheet */}
      <Sheet open={!!editUser} onOpenChange={(open) => { if (!open) { setEditUser(null); setEditPhotoOpen(false); } }}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
          </SheetHeader>
          {editUser && (
            <div className="space-y-6 mt-4">
              {/* Edit details */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Details</p>
                <Input
                  placeholder="Full name"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
                <Input
                  type="tel"
                  placeholder="Phone number (for password reset)"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, phone: e.target.value }))
                  }
                />
                {divisions.length > 0 && (
                  <DivisionMultiPicker
                    divisions={divisions}
                    selected={editForm.divisionIds}
                    onChange={(ids) => setEditForm((f) => ({ ...f, divisionIds: ids }))}
                  />
                )}
                {jobTitles.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Job Title</label>
                    <SearchableSelect
                      value={editForm.jobTitleId}
                      onChange={(v) => setEditForm((f) => ({ ...f, jobTitleId: v }))}
                      allLabel="— None —"
                      allValue=""
                      options={jobTitles.map((jt) => ({ value: jt.id, label: jt.name }))}
                    />
                  </div>
                )}
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Save Changes
                </Button>
              </div>

              {/* Read-only profile details */}
              {(editUser.jobTitleName || editUser.birthDate || editUser.emergencyContactName || editUser.profilePhotoFileId) && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-medium text-muted-foreground">Profile Info</p>
                  <div className="flex items-center gap-3">
                    {editPhotoUrlData?.url ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditPhotoOpen(true)}
                          className="cursor-zoom-in rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <img
                            src={editPhotoUrlData.url}
                            alt={editUser.name}
                            className="h-12 w-12 rounded-full object-cover border border-border"
                          />
                        </button>
                        {editPhotoOpen && createPortal(
                          <div
                            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
                            onClick={() => setEditPhotoOpen(false)}
                          >
                            <img
                              src={editPhotoUrlData.url}
                              alt={editUser.name}
                              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                            />
                          </div>,
                          document.body
                        )}
                      </>
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-surface-hover flex items-center justify-center border border-border">
                        <Camera className="h-5 w-5 text-muted-foreground/40" />
                      </div>
                    )}
                    <div className="space-y-0.5 text-sm">
                      {editUser.jobTitleName && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Briefcase className="h-3.5 w-3.5" />
                          <span>{editUser.jobTitleName}</span>
                        </div>
                      )}
                      {editUser.birthDate && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Cake className="h-3.5 w-3.5" />
                          <span>DOB: {formatDateOnly(editUser.birthDate, undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {editUser.emergencyContactName && (
                    <div className="rounded-md bg-surface-base/50 border border-border px-3 py-2 text-xs text-muted-foreground">
                      <p className="font-medium uppercase tracking-wider text-[10px] mb-1">Emergency Contact</p>
                      <p>{editUser.emergencyContactName}
                        {editUser.emergencyContactRelationship && <span> ({editUser.emergencyContactRelationship})</span>}
                      </p>
                      {editUser.emergencyContactPhone && <p>{editUser.emergencyContactPhone}</p>}
                    </div>
                  )}
                </div>
              )}

              {/* Records: links to everything we keep on file for this person */}
              <UserRecords user={{ id: editUser.id, name: editUser.name }} isSuperAdmin={isSuperAdmin} />

              {/* Reset password */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Reset Password</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  This will log the user out of all devices. They will need to sign in again with the new password.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder="New password (min 8 chars)"
                    value={resetPw}
                    onChange={(e) => setResetPw(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleResetPassword}
                    disabled={resetting || resetPw.length < 8}
                  >
                    {resetting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Reset
                  </Button>
                </div>
              </div>

              {/* Deactivate / Reactivate */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center gap-2">
                  {editUser.banned ? (
                    <UserCheck className="h-4 w-4 text-green-600" />
                  ) : (
                    <Ban className="h-4 w-4 text-muted-foreground" />
                  )}
                  <p className="text-sm font-medium">
                    {editUser.banned ? "Reactivate Account" : "Deactivate Account"}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  {editUser.banned
                    ? "This user is currently deactivated and cannot log in. Reactivating will restore their access."
                    : "Deactivating will immediately log the user out and prevent them from signing in. Their data is preserved."}
                </p>
                <Button
                  size="sm"
                  variant={editUser.banned ? "default" : "outline"}
                  className={editUser.banned ? "" : "border-amber-300 text-amber-700 hover:bg-amber-50"}
                  onClick={handleToggleDeactivate}
                  disabled={toggling}
                >
                  {toggling && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  {editUser.banned ? (
                    <>
                      <UserCheck className="h-4 w-4 mr-1" />
                      Reactivate
                    </>
                  ) : (
                    <>
                      <Ban className="h-4 w-4 mr-1" />
                      Deactivate
                    </>
                  )}
                </Button>
              </div>

              {/* Admin notes */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center gap-2">
                  <StickyNote className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Admin Notes</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Notes are visible only to admins here, and shown to the worker on their dashboard (unread ones highlighted).
                </p>
                {workerNotes.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {workerNotes.map((n) => {
                      const responseMeta: Record<string, { label: string; cls: string }> = {
                        acknowledged: { label: "Acknowledged", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
                        completed: { label: "Completed", cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
                        need_clarification: { label: "Need Clarification", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
                      };
                      const resp = n.response ? responseMeta[n.response] : null;
                      return (
                        <div
                          key={n.id}
                          className={cn(
                            "rounded-md border px-3 py-2 text-xs",
                            n.isReadByWorker
                              ? "border-border bg-surface-base/50 text-muted-foreground"
                              : "border-amber-300/50 bg-amber-50/50 dark:bg-amber-900/10 text-text-primary"
                          )}
                        >
                          <div className="flex items-center justify-between mb-1 gap-2">
                            <span className="font-medium">{n.authorName}</span>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {resp ? (
                                <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-full", resp.cls)}>
                                  {resp.label}
                                </span>
                              ) : !n.isReadByWorker ? (
                                <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">unread</span>
                              ) : null}
                              <span className="text-muted-foreground">
                                {new Date(n.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                              </span>
                            </div>
                          </div>
                          <p className="whitespace-pre-wrap">{n.content}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note for this worker..."
                    rows={2}
                    disabled={savingNote}
                    className="flex-1 rounded-md border border-border bg-surface-base/50 px-2.5 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/50"
                  />
                  <Button
                    size="sm"
                    disabled={savingNote || !newNote.trim()}
                    onClick={handleSaveNote}
                    className="self-end"
                  >
                    {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Delete account — super admin only */}
              {isSuperAdmin && <div className="space-y-3 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-red-600" />
                  <p className="text-sm font-medium text-red-600">Delete Account</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Permanently delete this user account. This cannot be undone. Their FLHAs and other data will remain but will no longer be linked to an active account.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" disabled={deleting}>
                      {deleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Permanently
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {editUser.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete the account for <strong>{editUser.email}</strong> and remove all their login credentials.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => {
                          setDeleteUserConfirm({ id: editUser.id, name: editUser.name, email: editUser.email });
                        }}
                      >
                        Continue
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* User delete — step 2 */}
      <AlertDialog open={!!deleteUserConfirm} onOpenChange={(v) => !v && setDeleteUserConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the account for <strong>{deleteUserConfirm?.email}</strong>. You'll have 10 seconds to undo after confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!deleteUserConfirm) return;
                const user = deleteUserConfirm;
                setDeleteUserConfirm(null);
                setEditUser(null);
                deleteWithUndo(user.name, async () => {
                  setDeleting(true);
                  try {
                    await apiDelete(`/api/admin/users/${user.id}`);
                    setUsers((prev) => prev.filter((u) => u.id !== user.id));
                    toast.success(`${user.name} deleted permanently`);
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "Failed to delete user");
                  } finally {
                    setDeleting(false);
                  }
                });
              }}
            >
              Yes, delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Parse a single CSV line, handling quoted fields with commas.
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        result.push(current);
        current = "";
      } else {
        current += ch;
      }
    }
  }
  result.push(current);
  return result;
}

/**
 * Multi-division picker — chip-based selector.
 * The first selected division is the user's PRIMARY division (used for catalogs,
 * default cert assignment, etc.). Tap to add/remove. Tap the star to make
 * a different division primary.
 */
function DivisionMultiPicker({
  divisions,
  selected,
  onChange,
}: {
  divisions: Division[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const makePrimary = (id: string) => {
    onChange([id, ...selected.filter((s) => s !== id)]);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Divisions <span className="opacity-60">(tap to add, first one becomes primary)</span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {divisions.map((d) => {
          const isSelected = selected.includes(d.id);
          const isPrimary = selected[0] === d.id;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => toggle(d.id)}
              onDoubleClick={() => isSelected && !isPrimary && makePrimary(d.id)}
              className={cn(
                "px-2.5 py-1 rounded-full text-xs border transition-colors",
                isSelected
                  ? isPrimary
                    ? "bg-paris-red text-white border-paris-red"
                    : "bg-paris-red/15 text-paris-red-text border-paris-red/40"
                  : "bg-card text-muted-foreground border-border hover:border-muted-foreground"
              )}
              title={
                isPrimary
                  ? "Primary division"
                  : isSelected
                    ? "Double-click to make primary"
                    : "Click to add"
              }
            >
              {isPrimary && "★ "}
              {d.name}
            </button>
          );
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-[10px] text-muted-foreground italic">
          No divisions selected. Worker will see all divisions' content.
        </p>
      )}
    </div>
  );
}
